import { Tuple2 }		from "ts-base/tuple";
import { Fn }			from "ts-base/fn";
import { Objects }		from "ts-base/objects";
import { Arrays }		from "ts-base/arrays";
import { Sets }			from "ts-base/sets";

import { Permission }	from "@v3/data/permission";

/** R is a role, P a permission */
export type AccessControl<R extends string, P extends string>	= {
	/** permissions by name */
	permissions:			Record<P, Permission>,
	/** null means "an unknown or unauthenticated user" */
	availablePermissions:	(principal:Subject<R>)	=> ReadonlySet<Permission>,
	/** null means "an unknown or unauthenticated user" */
	accessAllowed:			(principal:Subject<R>) => (permission:Permission)	=> boolean,
};

export namespace AccessControl {
	/** pass the role as an explicit type parameter to ensure we only use valid roles */
	export const of	= <R extends string>() => <P extends string>(table:PermissionRoleTable<P, R>):AccessControl<R, P>	=> {
		const permissions:Record<P, Permission>	=
			Objects.unsafeTypedFromEntries(
				Objects.unsafeTypedKeys(table).map((key) =>
					[ key, Permission.brand(key) ] as const
				)
			);

		const getPermissions	= rolePermissions(table);

		const availablePermissions	= (subject:Subject<R>):ReadonlySet<Permission>	=> {
			const roles:ReadonlySet<R|Unauthenticated>	=
				subject === Unauthenticated
				?	Sets.ofN(Unauthenticated)
				:	Sets.of(subject.roles);
			return Sets.flatMap(roles)(getPermissions);
		};

		const accessAllowed	= (subject:Subject<R>) => (permission:Permission):boolean	=>
			availablePermissions(subject).has(permission);

		return {
			permissions,
			availablePermissions,
			accessAllowed,
		};
	};

	/** precalculate which role (including null representing the role of an unauthenticated user) have which permissions */
	const rolePermissions	= <R extends string, P extends string>(table:PermissionRoleTable<P, R>):Fn<R|Unauthenticated, ReadonlySet<Permission>>	=> {
		const list:ReadonlyArray<Tuple2<R|Unauthenticated, P>>	=
			Objects.unsafeTypedEntries(table).flatMap(([ permission, grants ]) =>
				grants.map((role) => [ role, permission ])
			);

		const map:ReadonlyMap<R|Unauthenticated, ReadonlySet<Permission>>	=
			Arrays.multiMapThen(list)(Fn.identity)(permissions =>
				Arrays.mappedSet(permissions)(Permission.brand)
			);

		const get	= (role:R|Unauthenticated):ReadonlySet<Permission>	=>
			map.get(role) ?? Sets.empty();

		return get;
	};
}

/** all data of a principal actually needed to check permissions */
export type Subject<R>	= Authenticated<R> | Unauthenticated;

/** an authenticated user has roles */
export type Authenticated<R>	= Readonly<{
	roles:	ReadonlyArray<R>,
}>;

/** sentinel value acting as a special role for unauthenticated users */
export const Unauthenticated:unique symbol	= Symbol("Unauthenticated");
export type Unauthenticated					= typeof Unauthenticated;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PermissionName<T>	= T extends AccessControl<infer P, any> ? P : never;

/** "everyone" includes unauthenticated users */
export type PermissionRoleTable<P extends string, R extends string>	= Record<P, ReadonlyArray<R|Unauthenticated>>;
